import { useState, type ReactNode } from 'react';
import { Alert, Button, Tooltip } from 'antd';
import type { AlertProps } from 'antd';
import { Info } from '@phosphor-icons/react';
import { tokens } from '../theme';

/**
 * Gợi ý dạng banner nhưng ĐÓNG được. Khi đóng, banner biến mất khỏi body và thay bằng một nút
 * "Gợi ý" gọn trên PageHeader (không để lại icon lọt thỏm giữa trang); bấm nút thì banner mở lại.
 * Trạng thái đóng/mở nhớ vào `localStorage` theo `id` nên vào lại trang vẫn giữ nguyên.
 *
 * Dùng ba mảnh cùng một `id` qua hook `useDismissibleHint`:
 *   const hint = useDismissibleHint('rewards-deduct-points');
 *   <PageHeader ... hint={<HintToggle hint={hint} />} />
 *   <PageBody> <HintBanner hint={hint} message=... description=... /> ... </PageBody>
 *
 * Chỉ dùng cho HINT tham khảo (loại info) — KHÔNG dùng cho cảnh báo nghiệp vụ hay lỗi, những thứ
 * đó phải luôn hiện (xem PageStates.tsx, các Alert warning/error trong form).
 */

const STORAGE_PREFIX = 'reski.hint.';

function readDismissed(id: string): boolean {
  try {
    return localStorage.getItem(STORAGE_PREFIX + id) === '1';
  } catch {
    // localStorage có thể bị chặn (chế độ riêng tư) — coi như chưa đóng, chỉ mất tính "nhớ"
    return false;
  }
}

function persistDismissed(id: string, dismissed: boolean): void {
  try {
    if (dismissed) localStorage.setItem(STORAGE_PREFIX + id, '1');
    else localStorage.removeItem(STORAGE_PREFIX + id);
  } catch {
    // Bỏ qua: không nhớ được thì vẫn chạy đúng trong phiên hiện tại
  }
}

export interface DismissibleHint {
  /** `true` = banner đang mở. */
  open: boolean;
  collapse: () => void;
  expand: () => void;
}

/** Giữ trạng thái đóng/mở của một hint và nhớ qua `localStorage`. Một `id` cho một hint. */
export function useDismissibleHint(id: string): DismissibleHint {
  const [open, setOpen] = useState(() => !readDismissed(id));
  return {
    open,
    collapse: () => {
      setOpen(false);
      persistDismissed(id, true);
    },
    expand: () => {
      setOpen(true);
      persistDismissed(id, false);
    },
  };
}

interface HintBannerProps {
  hint: DismissibleHint;
  message: ReactNode;
  description?: ReactNode;
  /** Mặc định `info` — hint chỉ nên là thông tin tham khảo. */
  type?: NonNullable<AlertProps['type']>;
  icon?: ReactNode;
  /** Khoảng cách dưới banner (mặc định gap section; kiểu `banner` mặc định 0). */
  gap?: number;
  /** Kiểu `banner` của antd: sát mép, không bo góc — dùng khi đặt trong Card padding 0. */
  banner?: boolean;
}

/** Banner trong PageBody (hoặc trong Card). Mở thì hiện Alert (có nút ×), đóng thì không chiếm chỗ. */
export function HintBanner({
  hint,
  message,
  description,
  type = 'info',
  icon,
  banner = false,
  gap = banner ? 0 : tokens.space.sectionGap,
}: HintBannerProps) {
  if (!hint.open) return null;
  return (
    <Alert
      type={type}
      showIcon
      banner={banner}
      icon={icon}
      closable
      onClose={hint.collapse}
      message={message}
      description={description}
      style={{ marginBottom: gap }}
    />
  );
}

/**
 * Nút mở lại (chỉ icon), đặt vào `hint` của PageHeader. Chỉ hiện khi banner đang đóng.
 * Tooltip giữ ý nghĩa khi đã bỏ chữ; hiệu ứng hover ở `.hint-toggle` (index.css).
 */
export function HintToggle({
  hint,
  tooltip = 'Hiện lại gợi ý',
}: {
  hint: DismissibleHint;
  tooltip?: string;
}) {
  if (hint.open) return null;
  return (
    <Tooltip title={tooltip}>
      <Button
        className="hint-toggle"
        size="small"
        type="text"
        shape="circle"
        aria-label={tooltip}
        onClick={hint.expand}
        icon={<Info size={16} weight="fill" aria-hidden />}
      />
    </Tooltip>
  );
}
